import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepReturnTo } from '../components/common/returnPath';
import api from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import LoadingSpinner from '../components/LoadingSpinner';
import LLMSuggest from '../components/common/LLMSuggest';
import SettingsLayout, {
  SettingsSection,
} from '../components/settings/SettingsLayout';
import KnowledgeBriefPanel from '../components/KnowledgeBriefPanel';
import KnowledgeSuggestLauncher from '../components/KnowledgeSuggestLauncher';
import DocumentsPanel from '../components/DocumentsPanel';
import ImagesPanel from '../components/ImagesPanel';
import type { Category, Topic } from '../types';

type Section =
  | 'general'
  | 'knowledge'
  | 'documents'
  | 'images'
  | 'maintenance'
  | 'danger';

const SECTIONS: SettingsSection[] = [
  { key: 'general', label: 'General' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'documents', label: 'Documents' },
  { key: 'images', label: 'Images' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'danger', label: 'Danger zone', danger: true },
];

export default function TopicSettings() {
  const { topicId: idParam, section: sectionParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshTopics } = useProject();
  const topicId = Number(idParam);
  const section = (sectionParam || 'general') as Section;

  const [topic, setTopic] = useState<Topic | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTopic = useCallback(async () => {
    try {
      const t = await api.getTopic(topicId);
      setTopic(t);
      // Chase parent FKs so the back URL has project+category+topic (the work
      // view's query-param router needs all three to resolve the topic tab).
      if (t.category_id) {
        try {
          const c = await api.getCategory(t.category_id);
          setCategory(c);
          if (c.project_id) {
            try {
              const p = await api.getProject(c.project_id);
              setProjectName(p.name);
            } catch { /* best-effort */ }
          }
        } catch {
          /* best-effort — breadcrumb falls back to topic-only URL */
        }
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load topic');
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    fetchTopic();
  }, [fetchTopic]);

  // The section rail must not eat the way out. `section` is a route segment, so
  // navigating to it drops the query string — and with it the `?from=` that the
  // header's back button is pointing at. Looking around the sections before
  // heading back is the normal case, not a reason to strand someone.
  const handleSectionChange = (key: string) => {
    const qs = keepReturnTo(searchParams, new URLSearchParams()).toString();
    navigate(`/topics/${topicId}/settings/${key}` + (qs ? `?${qs}` : ''));
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !topic) {
    return (
      <div className="p-6 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
        {error ?? 'Topic not found'}
      </div>
    );
  }

  // The workspace URLs for this topic and its parent, written the way the
  // workspace writes them, instead of going through `/projects?project=…` and
  // letting the legacy redirect translate. That hop DISCARDED the tab and could
  // only ever land on the evidence pane.
  //
  // `topicId` is a plain node id. `category.id` is not: it comes back from the
  // `/categories` shim as `legacy_category_id or topic.id`, so it keeps the
  // `category-` prefix that `parseNodeParam` resolves by legacy id.
  const backToTopic = category?.project_id
    ? `/subjects/${category.project_id}?node=${topicId}`
    : '/subjects';
  const backToCategory = category?.project_id
    ? `/subjects/${category.project_id}?node=category-${category.id}`
    : '/subjects';

  const breadcrumb: { label: string; to?: string }[] = [
    { label: 'Subjects', to: '/subjects' },
  ];
  if (category?.project_id) {
    breadcrumb.push({ label: projectName ?? 'Subject', to: `/subjects/${category.project_id}` });
  }
  if (category) {
    breadcrumb.push({ label: category.name, to: backToCategory });
  }
  breadcrumb.push({ label: topic.name, to: backToTopic });
  breadcrumb.push({ label: 'Settings' });

  return (
    <SettingsLayout
      title={topic.name}
      scopeLabel="Topic"
      breadcrumb={breadcrumb}
      backTo={backToTopic}
      backLabel="Back to topic"
      sections={SECTIONS}
      activeSection={section}
      onSectionChange={handleSectionChange}
    >
      {section === 'general' && (
        <GeneralSection
          topic={topic}
          onSaved={() => {
            fetchTopic();
            refreshTopics();
          }}
        />
      )}
      {section === 'knowledge' && (
        <>
          <KnowledgeSuggestLauncher
            scope="topic"
            scopeId={topicId}
            scopeName={topic.name}
          />
          <KnowledgeBriefPanel
            scope="topic"
            scopeId={topicId}
            scopeName={topic.name}
          />
        </>
      )}
      {section === 'documents' && (
        <DocumentsPanel topic_id={topicId} label="Topic Documents" />
      )}
      {section === 'images' && (
        <ImagesPanel topic_id={topicId} label="Topic Images" />
      )}
      {section === 'maintenance' && (
        <MaintenanceSection topicId={topicId} />
      )}
      {section === 'danger' && (
        <DangerSection
          topic={topic}
          onDeleted={() => {
            refreshTopics();
            // Up one, not out to the root — the parent node is the nearest
            // thing that still exists.
            navigate(backToCategory);
          }}
        />
      )}
    </SettingsLayout>
  );
}

// -----------------------------------------------------------------------------
// General — name, slug, description, content/discovery guidelines, active
// -----------------------------------------------------------------------------

function GeneralSection({
  topic,
  onSaved,
}: {
  topic: Topic;
  onSaved: () => void;
}) {
  const [name, setName] = useState(topic.name);
  const [slug, setSlug] = useState(topic.slug ?? '');
  const [description, setDescription] = useState(topic.description ?? '');
  const [contentGuidelines, setContentGuidelines] = useState(
    topic.content_guidelines ?? ''
  );
  const [discoveryGuidelines, setDiscoveryGuidelines] = useState(
    topic.discovery_guidelines ?? ''
  );
  const [includeKeywords, setIncludeKeywords] = useState(
    topic.include_keywords_in_build_context
  );
  const [isActive, setIsActive] = useState(topic.is_active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    name !== topic.name ||
    slug !== (topic.slug ?? '') ||
    description !== (topic.description ?? '') ||
    contentGuidelines !== (topic.content_guidelines ?? '') ||
    discoveryGuidelines !== (topic.discovery_guidelines ?? '') ||
    includeKeywords !== topic.include_keywords_in_build_context ||
    isActive !== topic.is_active;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateTopic(topic.id, {
        name,
        slug: slug || null,
        description: description || null,
        content_guidelines: contentGuidelines || null,
        discovery_guidelines: discoveryGuidelines || null,
        include_keywords_in_build_context: includeKeywords,
        is_active: isActive,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">General</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Identity, guidelines, and status.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Slug
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="auto-derived from name if blank"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Content guidelines
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            applied to all pages built in this topic
          </span>
        </label>
        <textarea
          value={contentGuidelines}
          onChange={(e) => setContentGuidelines(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Discovery guidelines
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            steers the keyword-discovery agent
          </span>
        </label>
        <textarea
          value={discoveryGuidelines}
          onChange={(e) => setDiscoveryGuidelines(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
        />
      </div>

      <LabelsEditor
        topicId={topic.id}
        topicName={topic.name}
        topicDescription={topic.description}
      />

      <div className="flex items-center gap-2">
        <input
          id="include-kw-context"
          type="checkbox"
          checked={includeKeywords}
          onChange={(e) => setIncludeKeywords(e.target.checked)}
          className="h-4 w-4"
        />
        <label htmlFor="include-kw-context" className="text-sm text-gray-700 dark:text-gray-300">
          Include this topic's keywords in the page-builder context
        </label>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setIsActive(!isActive)}
          className={`px-3 py-1.5 text-sm rounded ${
            isActive
              ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          {isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Labels editor — tag-input style, saves on every change.
// -----------------------------------------------------------------------------

function LabelsEditor({
  topicId,
  topicName,
  topicDescription,
}: {
  topicId: number;
  topicName?: string;
  topicDescription?: string | null;
}) {
  const [labels, setLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.getTopicLabels(topicId);
        setLabels(resp.labels.map((l) => l.label_value));
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load labels');
      } finally {
        setLoading(false);
      }
    })();
  }, [topicId]);

  const persist = async (next: string[]) => {
    setSaving(true);
    setError(null);
    try {
      await api.updateTopicLabels(topicId, next);
      setLabels(next);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save labels');
    } finally {
      setSaving(false);
    }
  };

  const addLabel = () => {
    const trimmed = input.trim().toLowerCase();
    if (!trimmed || labels.includes(trimmed)) {
      setInput('');
      return;
    }
    persist([...labels, trimmed]);
    setInput('');
  };

  const removeLabel = (label: string) => {
    persist(labels.filter((l) => l !== label));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Labels
        {saving && (
          <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">Saving…</span>
        )}
      </label>
      <div className="flex flex-wrap gap-1 mb-2 min-h-[28px]">
        {loading ? (
          <span className="text-xs text-gray-400 dark:text-gray-500">Loading…</span>
        ) : labels.length === 0 ? (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">No labels yet</span>
        ) : (
          labels.map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-sm bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded"
            >
              {label}
              <button
                type="button"
                onClick={() => removeLabel(label)}
                className="icon-btn text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300"
                aria-label={`Remove label ${label}`}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addLabel();
            }
          }}
          onBlur={addLabel}
          placeholder="Type a label and press Enter"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
        />
        <LLMSuggest
          variant="typeahead"
          context="topic_label"
          value={input}
          multiSelect
          additionalContext={{
            ...(topicName ? { topic_name: topicName } : {}),
            ...(topicDescription ? { topic_description: topicDescription } : {}),
            ...(labels.length ? { existing_labels: labels.join(', ') } : {}),
          }}
          onSelect={(s) => {
            const trimmed = s.trim().toLowerCase();
            if (trimmed && !labels.includes(trimmed)) {
              persist([...labels, trimmed]);
            }
            setInput('');
          }}
        />
      </div>
      {error && (
        <div className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</div>
      )}
    </div>
  );
}

function MaintenanceSection({ topicId }: { topicId: number }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<
    { deleted_count: number; keyword_count: number } | null
  >(null);

  const handleClear = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.clearTopicKeywordMetricRuns(topicId);
      setResult(r);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to clear metric runs');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Maintenance</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Operational actions that don't change topic content but affect how
          background workers treat its keywords.
        </p>
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded p-4 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Clear keyword metric run history
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Deletes the records that mark this topic's keywords (and keywords
            on its recommendations) as recently attempted by the Google
            Keyword Planner ingest worker. The worker will re-try them on its
            next loop. Useful after the worker exhausted its retries against
            an expired Google Ads token.
          </p>
        </div>
        {result && (
          <div className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded p-2">
            Cleared {result.deleted_count} run record
            {result.deleted_count === 1 ? '' : 's'} across{' '}
            {result.keyword_count} keyword
            {result.keyword_count === 1 ? '' : 's'}.
          </div>
        )}
        {error && (
          <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
            {error}
          </div>
        )}
        <button
          onClick={handleClear}
          disabled={busy}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60"
        >
          {busy ? 'Clearing…' : 'Clear metric run history'}
        </button>
      </div>
    </div>
  );
}

function DangerSection({
  topic,
  onDeleted,
}: {
  topic: Topic;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteTopic(topic.id);
      onDeleted();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete');
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-red-700 dark:text-red-300">Danger zone</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Deleting this topic removes its keywords, pages, and knowledge brief.
          This cannot be undone.
        </p>
      </div>
      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}
      {confirming ? (
        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            disabled={busy}
            className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Deleting…' : `Yes, delete "${topic.name}"`}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm rounded hover:bg-red-50 dark:hover:bg-red-900/30"
        >
          Delete topic
        </button>
      )}
    </div>
  );
}
